import React from "react";

import { faBusinessTime } from "@fortawesome/pro-regular-svg-icons/faBusinessTime";
import { faLightbulb } from "@fortawesome/pro-solid-svg-icons/faLightbulb";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Deal from "@mapmycustomers/shared/types/entity/Deal";

import BoardColumn from "@app/component/DnDBoard/types/BoardColumn";
import localSettings from "@app/config/LocalSettings";
import AdjustedAmountTooltip from "@app/scene/deal/component/AdjustedAmountTooltip";
import StageColumn from "@app/scene/deal/type/StageColumn";

import styles from "./ColumnSummaryInfo.module.scss";

interface Props {
  column: BoardColumn<StageColumn, Deal>;
}

const ColumnSummaryInfo: React.FC<Props> = ({ column }) => {
  const intl = useIntl();

  const rottingInterval = column.data.stage.rottingInterval;
  const { adjustedAmount, amount } = column.data;

  return (
    <div className={styles.container}>
      <div className={styles.money}>
        <div className={styles.openAmount}>
          {intl.formatNumber(amount ?? 0, {
            currency: localSettings.getCurrencyCode(),
            minimumFractionDigits: 0,
            style: "currency",
          })}
        </div>
        <AdjustedAmountTooltip>
          <div className={styles.adjustedAmount}>
            <FontAwesomeIcon icon={faLightbulb} />
            <span>
              {intl.formatMessage(
                {
                  id: "deals.kanbanView.column.adjustedAmount",
                  defaultMessage: "{value} adjusted",
                  description: "Adjusted Amount value of subheader on Deal kanban page",
                },
                {
                  value: intl.formatNumber(adjustedAmount ?? 0, {
                    currency: localSettings.getCurrencyCode(),
                    minimumFractionDigits: 0,
                    style: "currency",
                  }),
                }
              )}
            </span>
          </div>
        </AdjustedAmountTooltip>
      </div>
      <div className={styles.columnStageData}>
        <div>
          <Tooltip
            placement="bottom"
            title={intl.formatMessage(
              {
                id: "deals.kanbanView.column.stageWeight.tooltip.title",
                defaultMessage: "Stage weight: <b>{stageWeight}</b>",
                description: "Title of subheader on Deal Kanban page",
              },
              {
                b: (value) => <span className={styles.stageWeight}>{value}</span>,
                stageWeight: intl.formatNumber(column.data.stage.weight, {
                  maximumFractionDigits: 0,
                  style: "percent",
                }),
              }
            )}
          >
            <div className={styles.stageWeightPercent}>
              {intl.formatNumber(column.data.stage.weight, {
                maximumFractionDigits: 0,
                style: "percent",
              })}
            </div>
          </Tooltip>
        </div>

        {rottingInterval && (
          <div>
            <Tooltip
              placement="bottom"
              title={intl.formatMessage(
                {
                  id: "deals.kanbanView.column.rotLimit.tooltip.title",
                  defaultMessage:
                    "Rot limit for this stage: <b>{rottingInterval} {rottingInterval, plural, one {day} other {days}}</b>",
                  description: "Title of subheader on Deal Kanban page",
                },
                { b: (value) => <span className={styles.rotLimit}>{value}</span>, rottingInterval }
              )}
            >
              <FontAwesomeIcon className={styles.rotLimitIcon} icon={faBusinessTime} size="sm" />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnSummaryInfo;
